<script setup lang="ts">
import { cn } from '#shared/lib/cn'
import { vMaska } from 'maska/vue'
import { ref } from 'vue'

type InputMode = 'text' | 'search' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal'

interface InputProps {
  label: string
  placeholder?: string
  type?: string
  inputmode?: InputMode
  autocomplete?: string
  inputMask?: string
  inputId?: string
  inputName?: string
  inputDataRole?: string
  containerClass?: string
  labelClass?: string
  inputClass?: string
}

withDefaults(defineProps<InputProps>(), {
  placeholder: '',
  type: 'text',
  inputmode: 'none',
  autocomplete: '',
  inputMask: '',
  containerClass: '',
  labelClass: '',
  inputClass: '',
})

const emit = defineEmits<{
  (eventName: 'enter'): void
}>()

const modelValue = defineModel<string>({ required: true })

const inputElementRef = ref<HTMLInputElement | null>(null)

defineExpose({
  inputElementRef,
})

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key !== 'Enter') return
  emit('enter')
}
</script>

<template>
  <label :class="cn('block', containerClass)">
    <span :class="cn('sr-only', labelClass)">{{ label }}</span>

    <input
      ref="inputElementRef"
      v-model.trim="modelValue"
      v-maska
      :id="inputId"
      :name="inputName"
      :data-roistat-field="inputDataRole"
      :data-maska="inputMask || undefined"
      :type="type"
      :inputmode="inputmode"
      :placeholder="placeholder || label"
      :autocomplete="autocomplete"
      :class="
        cn(
          `
            bg-brand-dark/1 text-brand-dark placeholder:text-brand-grey-light
            focus:bg-brand-dark/1 focus:ring-brand-red h-16 w-full rounded-2xl
            px-6 text-base ring-0 transition outline-none focus:ring-2 focus:outline-none
          `,
          inputClass
        )
      "
      @keydown="handleKeydown" />
  </label>
</template>
